import {
  IMachineContext,
  IStateMetaData,
  TInputTypes,
  TMachineActions,
  TMachineEvents,
  TMachineStates,
  TRefEventStates,
  TRefEventsActions,
} from '@/types';
import type { AnyEventObject, BaseActionObject, StateNodeConfig } from 'xstate';
import {
  handleBackFromRefSelect,
  handleChains,
  handleChallenge,
  handlePenalty,
  handleUnderReview,
  refHandleOutcome,
} from './refUtil';
import { TPATPlayEvents, TPATStates } from '@/types/workflow/patMachine';
import { MAIN_MACHINE_ID } from '@/constants';

export const PAT_REF_WF: StateNodeConfig<IMachineContext, any, AnyEventObject, BaseActionObject> = {
  initial: TRefEventStates.SELECT_REF_TYPE,
  states: {
    [TRefEventStates.SELECT_REF_TYPE]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Ref Event',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: refHandleOutcome,
        onBack: handleBackFromRefSelect,
      } as IStateMetaData,
      on: {
        [TPATPlayEvents.BACK_TO_PAT_PLAY_DESIGN]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}.${TPATStates.PAT_PLAY_DESIGN}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_XP_SNAP]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}.${TPATStates.XP_ATTEMPT_SNAP}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_TWO_PT_SNAP]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}.${TPATStates.TWO_PT_ATTEMPT_SNAP}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_CONFIRMATION]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}.${TPATStates.CONFIRM_PAT}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.ON_UNDER_REVIEW]: {
          target: TRefEventStates.UNDER_REVIEW,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.ON_CHALLENGE]: {
          target: TRefEventStates.CHALLANGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.PENALTY_SELECTED]: {
          target: TRefEventStates.PENALTY,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.ON_CHAINS]: {
          target: TRefEventStates.CHAINS,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },
    [TRefEventStates.UNDER_REVIEW]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Under Review',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: handleUnderReview,
        onBack({ send, context }) {
          send(context.inputForNextComponent.backTo, {});
        },
      } as IStateMetaData,
      on: {
        [TRefEventsActions.BACK]: {
          target: TRefEventStates.SELECT_REF_TYPE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.ON_OVERTURNED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.CLEAR_PLAY,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TRefEventsActions.ON_UNDER_REVIEW_SELECTED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_CHALLANGE]: {
          target: TRefEventStates.CHALLANGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
      },
    },
    [TRefEventStates.CHAINS]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Chain Measurement',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: handleChains,
        onBack({ send }) {
          send(TRefEventsActions.BACK, {});
        },
      } as IStateMetaData,
      on: {
        [TRefEventsActions.BACK]: {
          target: TRefEventStates.SELECT_REF_TYPE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
      },
    },
    [TRefEventStates.CHALLANGE]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Challenge',
          showLogo: false,
          showBackBtn: true,
        },
        onNext: handleChallenge,
        onBack({ send }) {
          send(TRefEventsActions.BACK, {});
        },
      } as IStateMetaData,
      on: {
        [TRefEventsActions.BACK]: {
          target: TRefEventStates.SELECT_REF_TYPE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.ON_CHALLENGE_SELECTED]: {
          target: TRefEventStates.UNDER_REVIEW,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },
    [TRefEventStates.PENALTY]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Penalty',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: handlePenalty,
        onBack({ send, context }) {
          send(context.inputForNextComponent.backTo, {});
        },
      } as IStateMetaData,
      on: {
        [TRefEventsActions.BACK]: {
          target: TRefEventStates.SELECT_REF_TYPE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_XP]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}.${TPATStates.XP_ATTEMPT}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_2PT]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}.${TPATStates.TWO_PT_ATTEMPT}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.ON_PENALTY_SELECTED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}.${TPATStates.SELECT_YARDAGE}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.PENALTY_DECLINED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.PENALTY_DECLINED_TO_CONFIRM]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}.${TPATStates.CONFIRM_PAT}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },
  },
};
